import {
  ApolloClient,
  ApolloClientOptions,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';

import generatedPossibleTypes from '__generated__/possibleTypes.json';

import {
  batchingLink,
  contextErrorLinks,
  dataIdFromObject,
  IgnorableError,
  promoteGQLErrorLink,
  retryLink,
} from 'platform/apolloLink';
import { UnauthenticatedError } from 'platform/Error';
import { globalTrackError } from 'platform/observability';

const { possibleTypes } = generatedPossibleTypes;

export const cacheOptions = {
  // When using fragments with unions and interfaces, the possible types for each polymorphic type must be passed to Apollo client.
  // Without this, fragments with unions and interfaces will not have their data cached and returned properly.
  // Documentation: https://www.apollographql.com/docs/react/data/fragments/#using-fragments-with-unions-and-interfaces
  // possibleTypes.json is generated based on the staging server's schema by running `yarn generate-possibleTypes`.
  possibleTypes,
  typePolicies: {
    Human: {
      keyFields: ['token'],
    },
    // Amplitude Experiments & Feature Flags
    OdProtosVariantServices_Variant_Query: {
      merge: true,
      OdProtosVariantServices_GetFeatureFlagsResponse_VariantsEntry: {
        keyFields: ['key'],
      },
      OdProtosVariantServices_GetVariantsResponse_VariantsEntry: {
        keyFields: ['key'],
      },
    },
    OlaQuery: {
      merge: true,
    },
    OlaOboOffer: {
      fields: {
        documents: {
          merge: false,
        },
        tasks: {
          merge: false,
        },
      },
    },
    OboClosingFaqsResult: {
      merge: true,
    },
    OdProtosBuyerServicesBuyer_Buyer_Query: {
      merge: true,
    },
    OdProtosWebServicesSellDirect_SellDirect_Query: {
      merge: true,
    },
    Property: {
      keyFields: ['addressId'],
      merge: true,
    },
    CloseOfEscrowHolidaysResponse: {
      merge: true,
    },
  },
};

// https://grpc.github.io/grpc/core/md_doc_statuscodes.html
const BLOCKED_RETRY_STATUS_NUMBERS = [
  // OK
  0,
  // INVALID_ARGUMENT
  3,
  // NOT_FOUND
  5,
  // PERMISSION_DENIED
  7,
  // UNAUTHENTICATED
  16,
];

const IGNORABLE_ERRORS_LIST: Array<IgnorableError> = [
  // NOTE: This occurs when markets are added and do not have adequate data yet.
  {
    value: 'No entry found for market',
  },
  // NOTE: This occurs when markets are added and do not have adequate data yet.
  {
    value: 'No off market listings found',
  },
  // NOTE: This occurs Ballpark just doesn't have home price data for an unqualified home
  {
    value: 'BallparkQuery: 5 NOT_FOUND',
  },
  // NOTE: This occurs when a user has not provided a phone number when creating a BwOD offer
  {
    value: 'Unable to save offer',
  },
  // NOTE: This occurs when someone tries to view a lead owned by a different
  // customer. This is an expected error and is handled gracefully, so no need
  // to report it.
  {
    value: 'GetLatestLead - failed to find lead',
  },
  // NOTE: This occurs when we try to fetch a trait for a lead for whom Journey
  // currently has no traits. This is expected and handled gracefully.
  {
    value: '5 NOT_FOUND: GetTraitForIdentifier',
  },
];

export const apolloCache = new InMemoryCache({ ...cacheOptions, dataIdFromObject });

export const clientOptions = (
  uri: string,
  opts?: { noBatch?: boolean },
): ApolloClientOptions<NormalizedCacheObject> => ({
  name: 'cake',
  link: ApolloLink.from([
    ...contextErrorLinks({
      ignorableErrorsList: IGNORABLE_ERRORS_LIST,
      trackError: globalTrackError,
    }),
    retryLink({}),
    promoteGQLErrorLink(BLOCKED_RETRY_STATUS_NUMBERS),
    redirectUnauthenticatedErrors(),
    opts?.noBatch ? createHttpLink({ uri }) : batchingLink(uri),
  ]),
  cache: apolloCache,
  defaultOptions: {
    // This no-cache is required since the query doesn't have appropriate ids on each part of the response
    // See https://www.apollographql.com/docs/react/caching/cache-configuration/#generating-unique-identifiers
    // TODO: Remove after each query is audited to include an id
    query: { errorPolicy: 'all', fetchPolicy: 'no-cache' },
    mutate: { errorPolicy: 'all' },
  },
});

/**
 * Redirects unauthenticated errors to the login page
 */
export const redirectUnauthenticatedErrors = () =>
  new ApolloLink((operation, forward) =>
    forward(operation).map((data) => {
      data?.errors?.forEach((error) => {
        if (error.message.includes('16 UNAUTHENTICATED')) {
          throw new UnauthenticatedError(operation.operationName, 'apollo-link');
        }
      });
      return data;
    }),
  );

const DEFAULT_GRAPHQL_CLIENT_URI = '/api/graphql';

/**
 * Set up an ApolloClient given a URI
 *
 * This client provides support for HTTP endpoints and socket subscriptions
 */
export const clientFromUrl = (
  opts?: { noBatch?: boolean } & Partial<ApolloClientOptions<NormalizedCacheObject>>,
): ApolloClient<NormalizedCacheObject> =>
  new ApolloClient(clientOptions(DEFAULT_GRAPHQL_CLIENT_URI, opts));
